/**
 * Estágio comercial da oportunidade: histórico + próximos passos (Task 13).
 *
 * Duas responsabilidades, e nenhuma delas é "decidir a regra":
 *
 * 1. **Mostrar o histórico** do estágio (`crm_opportunity_events`, migration
 *    0032): de onde para onde, QUEM mudou, quando e por quê. Mudança de origem
 *    automática aparece como `system` — a tela não a apresenta como ação de
 *    operador.
 * 2. **Oferecer o que o pipeline permite**: destinos e campos exigidos vêm de
 *    `nextStages`/`stageRequirementsFor` (módulo puro), não de uma lista de
 *    botões mantida aqui. Estágio fechado não oferece ação.
 *
 * Como nas Tasks 11 e 12, o caminho de gravação entra por prop (`stage`): sem
 * ele a tela é leitura e DIZ que a gravação não está montada, em vez de exibir
 * um botão que não grava.
 *
 * Redesign R6 (apresentação apenas): o estágio vira um cartão tokenizado no
 * padrão aprovado (`rounded-panel border border-border bg-surface p-4
 * shadow-s1`); o estágio atual é um SELO ícone+texto com accent de marca
 * (vermelho via `brand`, nunca só cor); a timeline é uma lista com separador
 * neutro; os formulários de transição usam campos/botões tokenizados. Todos os
 * `data-*` (incl. `data-opportunity-stage`, `data-stage-to`, `data-stage-field`,
 * `data-action`), os textos, o `LEAD_STAGE_LABELS` e o contrato de submit são
 * PRESERVADOS — as classes hook seguem no markup como ganchos estáveis.
 *
 * Migrado para `.tsx`/JSX real (Cluster C — Atendimento & CRM, redesign por
 * perfil; ver `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe apenas
 * (`createElement` → JSX): os ícones (`Circle`, `Filter`) já existiam.
 */
// Import explícito de `React` — ver o mesmo comentário em `AppShell.tsx`.
import React, { type FormEvent, type ReactElement } from "react";
import { Circle, Filter } from "lucide-react";
import { StatusPanel } from "./StatusPanel.js";
import {
  checkStageTransition,
  isClosedStage,
  LEAD_STAGE_LABELS,
  nextStages,
  STAGE_FIELD_LABELS,
  stageRequirementsFor,
  type LeadStage,
  type StageTransitionCheck,
  type StageTransitionField,
  type StageTransitionInput
} from "../crm/pipeline.js";
import { opportunityTimeline } from "../crm/dataset.js";
import { formatDateTime } from "../format.js";
import type { OpportunityStageHandle } from "../crm/view-props.js";
import type { CrmOpportunity } from "../api/types.js";

export interface OpportunityStageProps {
  opportunity: CrmOpportunity;
  stage?: OpportunityStageHandle;
}

/** Só o que o formulário precisa do `FormData` — torna o submit testável. */
export interface StageFormValues {
  get(name: string): unknown;
}

export type StageSubmission =
  | { ok: true; input: { to: LeadStage } & StageTransitionInput }
  | { ok: false; check: StageTransitionCheck };

/**
 * O caminho de submit REAL da mudança de estágio, isolado do evento de DOM
 * (que não existe sem navegador): `onSubmit` abaixo só traduz o evento para cá.
 *
 * A validação usa a MESMA `checkStageTransition` do servidor. Isso não
 * substitui a checagem server-side (a autoridade é o servidor); evita mandar
 * uma transição que já se sabe inválida e diz ao operador qual campo falta.
 *
 * O ator não é montado aqui: quem mudou o estágio vem da sessão no servidor.
 */
export function submitOpportunityStage(
  opportunity: CrmOpportunity,
  to: LeadStage,
  values: StageFormValues,
  stage: OpportunityStageHandle
): StageSubmission {
  const input: StageTransitionInput = {};
  for (const field of stageRequirementsFor(to)) {
    const raw = values.get(field);
    if (typeof raw === "string") input[field] = raw.trim();
  }
  const observation = values.get("reason");
  if (typeof observation === "string" && observation.trim()) input.reason = observation.trim();
  const check = checkStageTransition(opportunity.stage, to, input);
  if (!check.ok) return { ok: false, check };
  const payload = { to, ...input };
  stage.submit(payload);
  return { ok: true, input: payload };
}

function fieldInput(to: LeadStage, field: StageTransitionField, disabled: boolean): ReactElement {
  const id = `stage-${to}-${field}`;
  return (
    <p key={field} className="stage-transition__field flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-semibold uppercase tracking-wide text-muted">
        {STAGE_FIELD_LABELS[field]}
      </label>
      <input
        id={id}
        name={field}
        type="text"
        required
        disabled={disabled}
        data-stage-field={field}
        className="rounded-control border border-border bg-surface px-3 py-2 text-sm text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60"
      />
    </p>
  );
}

function StageForm({
  opportunity,
  to,
  stage
}: {
  opportunity: CrmOpportunity;
  to: LeadStage;
  stage: OpportunityStageHandle;
}): ReactElement {
  const required = stageRequirementsFor(to);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    submitOpportunityStage(opportunity, to, new FormData(event.currentTarget), stage);
  }

  return (
    <form
      className="stage-transition flex flex-col gap-3 rounded-control border border-border bg-surface-soft p-3"
      data-stage-to={to}
      onSubmit={onSubmit}
    >
      <h4 className="text-sm font-semibold text-ink">{`Mover para: ${LEAD_STAGE_LABELS[to]}`}</h4>
      {required.length ? (
        <div className="stage-transition__requirements flex flex-col gap-3" data-stage-requires={required.join(",")}>
          {required.map((field) => fieldInput(to, field, stage.pending))}
        </div>
      ) : (
        <p className="stage-transition__requirements text-sm text-muted" data-stage-requires="">
          Nenhum dado adicional exigido.
        </p>
      )}
      <button
        type="submit"
        disabled={stage.pending}
        data-action={`stage-${to}`}
        className="inline-flex items-center justify-center self-start rounded-control bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors duration-fast ease-s2 hover:bg-brand-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-60"
      >
        {stage.pending ? "Registrando…" : `Registrar ${LEAD_STAGE_LABELS[to].toLowerCase()}`}
      </button>
    </form>
  );
}

/** Dado que justificou a mudança, exibido em texto (nunca escondido). */
function payloadSummary(payload: Record<string, unknown>): string {
  const entries = Object.entries(payload).filter(([, value]) => value !== null && value !== undefined && value !== "");
  if (!entries.length) return "sem dado adicional";
  return entries
    .map(([key, value]) => `${STAGE_FIELD_LABELS[key as StageTransitionField] ?? key}: ${String(value)}`)
    .join(" · ");
}

/**
 * Selo do estágio atual: ícone de funil + texto, accent de marca (vermelho via
 * `brand`). O texto carrega a informação (WCAG 1.4.1); a cor é reforço.
 */
function StageBadge({ stage }: { stage: LeadStage }): ReactElement {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-pill border border-brand bg-brand-wash px-2 py-0.5 text-xs font-semibold text-brand-text">
      <Filter className="h-3.5 w-3.5 shrink-0" aria-hidden focusable={false} />
      <span>{LEAD_STAGE_LABELS[stage]}</span>
    </span>
  );
}

export function OpportunityStage({ opportunity, stage }: OpportunityStageProps): ReactElement {
  const timeline = opportunityTimeline(opportunity);
  const destinations = nextStages(opportunity.stage);
  return (
    <section
      className="opportunity-stage flex flex-col gap-4 rounded-panel border border-border bg-surface p-4 shadow-s1"
      data-section="estagio-comercial"
    >
      <h3 className="text-lg font-extrabold text-ink">Estágio comercial</h3>
      <p
        className="page__note flex flex-wrap items-center gap-2 text-sm text-muted"
        data-opportunity-id={opportunity.id}
        data-opportunity-stage={opportunity.stage}
        data-opportunity-owner={opportunity.ownerProfileId}
      >
        {"Estágio atual: "}
        <StageBadge stage={opportunity.stage} />
        {` · responsável: ${opportunity.ownerLabel}`}
      </p>
      <h4 className="text-sm font-semibold text-ink">Histórico do estágio</h4>
      {timeline.length ? (
        <ol className="stage-timeline m-0 flex list-none flex-col p-0">
          {timeline.map((event, index) => (
            <li
              key={event.id}
              className="stage-timeline__item relative flex gap-3 pb-4 last:pb-0"
              data-record-id={event.id}
              data-stage-event-to={event.toStage}
              data-stage-event-actor-kind={event.actorKind}
            >
              <span className="relative flex w-4 shrink-0 flex-col items-center" aria-hidden>
                <Circle className="mt-0.5 h-3 w-3 shrink-0 fill-brand text-brand" />
                {index < timeline.length - 1 ? <span className="mt-1 w-px flex-1 bg-border" /> : null}
              </span>
              <p className="stage-timeline__content m-0 flex-1 rounded-control bg-surface-soft p-3 text-sm text-body">
                <span className="stage-timeline__stages font-semibold text-ink">
                  {event.fromStage
                    ? `${LEAD_STAGE_LABELS[event.fromStage]} → ${LEAD_STAGE_LABELS[event.toStage]}`
                    : `Oportunidade aberta em ${LEAD_STAGE_LABELS[event.toStage]}`}
                </span>
                {" · "}
                <span className="stage-timeline__actor">
                  {event.actorKind === "staff" ? `registrado por ${event.actor}` : `mudança de origem automática (${event.actor})`}
                </span>
                {" · "}
                <time dateTime={event.occurredAt}>{formatDateTime(event.occurredAt)}</time>
                {event.reason ? <span className="stage-timeline__reason">{` · motivo: ${event.reason}`}</span> : null}
                <span className="stage-timeline__payload text-muted">{` · ${payloadSummary(event.payload)}`}</span>
              </p>
            </li>
          ))}
        </ol>
      ) : (
        <StatusPanel
          kind="empty"
          title="Nenhuma mudança de estágio registrada"
          detail="A oportunidade existe, mas ainda não tem evento — nada foi inferido no lugar do histórico."
        />
      )}
      <h4 className="text-sm font-semibold text-ink">Próximos passos</h4>
      {isClosedStage(opportunity.stage) ? (
        <StatusPanel
          kind="empty"
          title={`Negociação encerrada: ${LEAD_STAGE_LABELS[opportunity.stage]}`}
          detail={
            "Nenhum estágio sai daqui. Nova demanda do mesmo cliente abre uma oportunidade NOVA, com o seu próprio " +
            "histórico — reabrir sobrescreveria um resultado já contado no funil."
          }
        />
      ) : stage ? (
        <div className="opportunity-stage__transitions grid gap-3 sm:grid-cols-2">
          {destinations.map((to) => (
            <StageForm key={to} opportunity={opportunity} to={to} stage={stage} />
          ))}
        </div>
      ) : (
        <StatusPanel
          kind="awaiting_source"
          title="Gravação de estágio não montada nesta visualização"
          detail={`Deste estágio a negociação pode seguir para: ${destinations.map((to) => LEAD_STAGE_LABELS[to]).join(", ")}. Esta montagem é só leitura.`}
        />
      )}
      {stage?.error ? <StatusPanel kind="error" title="O estágio não foi alterado" detail={stage.error.message} /> : null}
      {stage?.savedStage ? (
        <StatusPanel kind="empty" title={`Estágio registrado: ${LEAD_STAGE_LABELS[stage.savedStage]}`} />
      ) : null}
    </section>
  );
}
